/** ⌘K search over the state's offline search index (search.json sidecar).
 * search.json (verified against a live fetch of
 * https://tiles.dustlark.app/utah/search.json, and against
 * ios/Dustlark/Map/SearchIndex.swift's parseEntries, the field authority)
 * is a bare JSON array of {n,k,x,y} — no {entries:[...]} wrapper. Ranking
 * mirrors SearchIndex.search(): prefix matches before substring matches. */
import { useEffect, useMemo, useRef, useState } from "react";
import { TILES, type StateEntry } from "../catalog";

export interface Entry {
  n: string; // name
  k: string; // kind — SearchIndex.swift defaults a missing "k" to "place"
  x: number; // lon
  y: number; // lat
}

const MIN_QUERY_LENGTH = 2;
const MAX_RESULTS = 50;
const DEBOUNCE_MS = 100;

export default function SearchPalette({
  state,
  onJump,
  onClose,
}: {
  state: StateEntry;
  onJump: (lon: number, lat: number, zoom: number) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [error, setError] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRowRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    let cancelled = false;
    fetch(`${TILES}/${state.region}/search.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j) => {
        if (!cancelled) setEntries(normalize(j));
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [state.region]);

  // Debounce the QUERY used for matching (not the input's own value, which
  // stays instant) — a state's search.json can carry 15k+ entries, and
  // filtering all of them on every keystroke is the kind of jank a 100ms
  // debounce exists to avoid.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [q]);

  const { hits, truncated } = useMemo(
    () => search(entries, debouncedQ),
    [entries, debouncedQ],
  );

  // A fresh result set always starts highlighted on the top row.
  useEffect(() => setActiveIndex(0), [hits]);

  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const jumpTo = (h: Entry) => onJump(h.x, h.y, 13);

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          value={q}
          placeholder={`Search ${state.name}…`}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              // stopPropagation matters here: unlike the other sheets,
              // SEARCH has no draw-mode gate (its pill has no `disabled`,
              // and ⌘K works globally — see App.tsx) — without this,
              // Escape while mid-draw would ALSO reach App's window-level
              // draw-mode handler and deselect a point or open the discard
              // prompt in the same keystroke.
              e.stopPropagation();
              onClose();
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              setActiveIndex((i) => Math.min(i + 1, hits.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === "Enter") {
              const hit = hits[activeIndex] ?? hits[0];
              if (hit) jumpTo(hit);
            }
          }}
        />
        {error && <p className="palette-note">Search index unavailable for this state.</p>}
        <ul>
          {hits.map((h, i) => (
            <li key={`${h.n}-${h.x}-${h.y}`}>
              <button
                ref={i === activeIndex ? activeRowRef : undefined}
                className={i === activeIndex ? "row active" : "row"}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => jumpTo(h)}
              >
                <span>{h.n}</span>
                <span className="chip">{h.k}</span>
              </button>
            </li>
          ))}
        </ul>
        {truncated && (
          <p className="palette-note">Showing the first {MAX_RESULTS} matches — keep typing to narrow it down.</p>
        )}
      </div>
    </div>
  );
}

/** Prefix matches rank above substring matches, same as
 * SearchIndex.search() — matches the whole entry set, then caps the
 * rendered list at MAX_RESULTS (`truncated` tells the UI there's more). */
export function search(entries: Entry[] | null, query: string): { hits: Entry[]; truncated: boolean } {
  const trimmed = query.trim().toLowerCase();
  if (!entries || trimmed.length < MIN_QUERY_LENGTH) return { hits: [], truncated: false };
  const prefix: Entry[] = [];
  const contains: Entry[] = [];
  for (const e of entries) {
    const name = e.n.toLowerCase();
    if (name.startsWith(trimmed)) prefix.push(e);
    else if (name.includes(trimmed)) contains.push(e);
  }
  const all = prefix.concat(contains);
  return { hits: all.slice(0, MAX_RESULTS), truncated: all.length > MAX_RESULTS };
}

/** search.json is a bare array of {n,k,x,y} (verified live, and against
 * SearchIndex.swift's parseEntries) — "k" defaults to "place" when a row
 * omits it, mirroring `i["k"] as? String ?? "place"`. The {entries:[...]}
 * fallback stays as cheap insurance against a future factory-era shape. */
export function normalize(j: unknown): Entry[] {
  const arr = Array.isArray(j) ? j : ((j as { entries?: unknown[] })?.entries ?? []);
  return (arr as Record<string, unknown>[])
    .map((e) => ({
      n: String(e.n ?? ""),
      k: e.k !== undefined ? String(e.k) : "place",
      x: Number(e.x),
      y: Number(e.y),
    }))
    .filter((e) => e.n && Number.isFinite(e.x) && Number.isFinite(e.y));
}
